import { notify } from '@/components/composed/notify';
import { ConfirmDialog } from '@/components/composed/ConfirmDialog';
import { ApiError } from '@/lib/api/errors';
import { useVoidInvoice } from '../api/invoice-mutations';
import type { InvoiceId } from '@/lib/api/ids';

/*
 * Destructive confirm for a financial action (spec §7.2, §7.4). Pessimistic:
 * the mutation invalidates on success. 409 (e.g. already paid) surfaces the
 * server message.
 */
export function VoidInvoiceConfirm({
  invoiceId,
  invoiceNumber,
  open,
  onOpenChange,
}: {
  invoiceId: InvoiceId;
  invoiceNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const voidInvoice = useVoidInvoice(invoiceId);

  async function onConfirm() {
    try {
      await voidInvoice.mutateAsync();
      notify.success(`Invoice ${invoiceNumber} voided.`);
    } catch (error) {
      notify.error(
        error instanceof ApiError ? error.message : 'Failed to void invoice.',
      );
      throw error;
    }
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Void invoice ${invoiceNumber}?`}
      description="This cannot be undone. The invoice will be marked void and excluded from billing totals."
      confirmLabel="Void invoice"
      destructive
      onConfirm={onConfirm}
    />
  );
}
